import { Badge, Center, Loader, Table, Text } from '@mantine/core';
import { IconCalendarMonth } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { SectionCard } from '../SectionCard';
import { formatSom } from '../../lib/format';
import { monthLabel } from '../../lib/month';

/** Backend `GET /payroll/oylar/:userId` qatori. */
interface OyQisqa {
  month: string;
  configured: boolean;
  type: string | null;
  ishKunlari: number;
  kelganKunlar: number;
  kelmaganKunlar: number;
  qoshimchaKunlar: number;
  ishladiDaqiqa: number;
  ketishYoqKunlar: number;
  amount: number;
  paid: number;
  qolgan: number;
  prognoz: number;
}

/**
 * OYLAR BO'YICHA — oxirgi 6 oy: necha kun kelgan, qancha ishlagan, qancha yozilgan, berilgan
 * va qolgan. Qator bosilsa o'sha oy tanlanadi (pastdagi hisob va kunlar jadvali o'sha oyga o'tadi).
 * Raqamlar — xodim kartasi bilan AYNI server hisobi (`computeSalary`).
 */
export function OylarJadvali({ userId, month, onSelect }: { userId: string; month: string; onSelect: (m: string) => void }) {
  const { t } = useTranslation();
  const oyNomlari = t('monthsFull', { returnObjects: true }) as string[];
  const { data, isLoading } = useQuery({
    queryKey: ['salary-oylar', userId],
    queryFn: async () => (await api.get<OyQisqa[]>(`/payroll/oylar/${userId}`, { params: { soni: 6 } })).data,
  });
  const hm = (min: number) => t('kunlar.h', { h: Math.floor(min / 60), m: min % 60 });

  return (
    <SectionCard title={t('kunlar.monthsTitle')} subtitle={t('kunlar.monthsSubtitle')} icon={IconCalendarMonth} p={0}>
      {isLoading ? (
        <Center py="md"><Loader size="sm" /></Center>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="xs" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('kunlar.month')}</Table.Th>
                <Table.Th>{t('kunlar.came')}</Table.Th>
                <Table.Th>{t('kunlar.absent')}</Table.Th>
                <Table.Th>{t('kunlar.worked')}</Table.Th>
                <Table.Th ta="right">{t('kunlar.accrued')}</Table.Th>
                <Table.Th ta="right">{t('kunlar.paid')}</Table.Th>
                <Table.Th ta="right">{t('kunlar.remaining')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((o) => {
                const tanlangan = o.month === month;
                return (
                  <Table.Tr
                    key={o.month}
                    onClick={() => onSelect(o.month)}
                    style={{ cursor: 'pointer', ...(tanlangan ? { background: 'var(--mantine-color-blue-light)' } : {}) }}
                  >
                    <Table.Td>
                      <Text size="sm" fw={tanlangan ? 800 : 600}>{monthLabel(o.month, oyNomlari)}</Text>
                      {tanlangan && <Text size="xs" c="blue">{t('kunlar.selected')}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>{t('kunlar.daysOf', { n: o.kelganKunlar, jami: o.ishKunlari })}</Text>
                      {o.qoshimchaKunlar > 0 && <Text size="xs" c="dimmed">{t('kunlar.extraDays', { n: o.qoshimchaKunlar })}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={o.kelmaganKunlar > 0 ? 'red.7' : 'dimmed'}>{o.kelmaganKunlar || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>{o.ishladiDaqiqa > 0 ? hm(o.ishladiDaqiqa) : '—'}</Text>
                      {o.ketishYoqKunlar > 0 && (
                        <Badge size="xs" color="orange" variant="light">{t('kunlar.noOutDays', { n: o.ketishYoqKunlar })}</Badge>
                      )}
                    </Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={700} style={{ whiteSpace: 'nowrap' }}>{o.configured ? formatSom(o.amount) : '—'}</Text></Table.Td>
                    <Table.Td ta="right"><Text size="sm" style={{ whiteSpace: 'nowrap' }}>{o.paid ? formatSom(o.paid) : '—'}</Text></Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={700} c={o.qolgan > 0 ? 'red.7' : o.qolgan < 0 ? 'orange.8' : 'teal.7'} style={{ whiteSpace: 'nowrap' }}>
                        {o.configured ? formatSom(o.qolgan) : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </SectionCard>
  );
}
